// src/Lib/Firebase.ts
import { initializeApp, getApps, FirebaseApp, FirebaseOptions } from "firebase/app";
import {
    getAuth,
    RecaptchaVerifier,
    Auth,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

const configuredAuthDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
const authDomain = typeof window !== "undefined" && window.location.hostname === "www.mockzo.com"
    ? "www.mockzo.com"
    : configuredAuthDomain;

const firebaseConfig: FirebaseOptions = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const requiredConfig = ["apiKey", "authDomain", "projectId", "appId"] as const;
const missingConfig = requiredConfig.filter((key) => !firebaseConfig[key]);
if (missingConfig.length > 0) {
    throw new Error(`Missing Firebase configuration: ${missingConfig.join(", ")}`);
}

/// --- Single Firebase app ---
export const firebaseApp: FirebaseApp = getApps().length
    ? getApps()[0]!
    : initializeApp(firebaseConfig);

// --- Single Auth + Firestore ---
export const auth: Auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

// OPTIONAL: enable test numbers bypass in dev (NO effect in prod)
// Set your test numbers in Firebase Console → Auth → Phone → Test numbers
if (typeof window !== "undefined") {
    const isLocalhost = /^(localhost|127\.0\.0\.1)/.test(window.location.hostname);
    try {
        // Only affect dev; in prod this does nothing harmful if left on false
        (auth as any).settings = (auth as any).settings || {};
        (auth as any).settings.appVerificationDisabledForTesting = isLocalhost;
    } catch { }
}

// --- Recaptcha management ---
let recaptcha: RecaptchaVerifier | null = null;

// internal guard that the container exists and we are in browser
function assertEnv() {
    if (typeof window === "undefined") {
        throw new Error("reCAPTCHA requires a browser environment.");
    }
    if (!document.getElementById("recaptcha-container")) {
        throw new Error("reCAPTCHA container #recaptcha-container is missing in the DOM.");
    }
}

export const ensureRecaptcha = async (visibleFallback = false): Promise<RecaptchaVerifier> => {
    assertEnv();

    // Destroy broken or mismatched instances
    const anyRec = recaptcha as any;
    if (anyRec?.destroyed) recaptcha = null;

    if (!recaptcha) {
        recaptcha = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: visibleFallback ? "normal" : "invisible",
            callback: () => { },
            "expired-callback": () => {
                try { (recaptcha as any)?.reset?.(); } catch { }
            },
        });
        // MUST render once
        try { await recaptcha.render(); } catch { }
    }
    return recaptcha!;
};

export const resetRecaptcha = () => {
    try { (recaptcha as any)?.clear?.(); } catch { }
    recaptcha = null;
};
